import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Plus, Users } from "lucide-react";
import { formatStartDate, isProjectMember, sortNewestFirst, useProjects, type Project } from "@lumanity/ui/projects";
import { ErrorBanner } from "@lumanity/ui/primitives";
import PageLayout from "@/components/PageLayout";
import { OutputLinks, useProjectOutputs } from "@/features/outputs";

/** One project: the card opens the project; its outputs (if any) open straight into each output. */
const ProjectCard = ({ project }: { project: Project }) => {
  const { data: outputs = [] } = useProjectOutputs(project.id);

  return (
    <div className="relative flex items-start justify-between bg-card px-5 py-5 hover:shadow-md transition-shadow group">
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-brand-navy group-hover:text-brand-teal transition-colors mb-2">
          {/* Stretched link: the whole card opens the project, the output buttons sit above it. */}
          <Link to={`/projects/${project.id}`} className="after:absolute after:inset-0">
            {project.name}
          </Link>
        </h3>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {project.code && <span className="font-medium text-brand-navy">{project.code}</span>}
          {project.start_date && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Started {formatStartDate(project.start_date)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {project.member_count} team {project.member_count === 1 ? "member" : "members"}
          </span>
        </div>
        <OutputLinks projectId={project.id} outputs={outputs} className="mt-4" />
      </div>
      {outputs.length === 0 && (
        <ArrowUpRight className="w-6 h-6 shrink-0 text-brand-navy group-hover:text-brand-teal transition-colors" />
      )}
    </div>
  );
};

/** Landing page: start a project, or open one of the user's projects. */
const Home = () => {
  const { data: projects = [], isLoading, error } = useProjects();
  // The service also lists every project to app admins; "My Projects" is only the ones they're on.
  const myProjects = sortNewestFirst(projects.filter(isProjectMember));

  return (
    <PageLayout mainClassName="container-md">
      <h2 className="text-xl text-brand-navy mb-4">Quick Actions</h2>
      <Link
        to="/projects/new"
        className="block bg-brand-light-teal p-6 mb-10 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand-teal flex items-center justify-center">
            <Plus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-brand-navy">Setup a New Project</h3>
            <p className="text-sm text-muted-foreground">Initiate a new project and configure workflow settings</p>
          </div>
        </div>
      </Link>

      <h2 className="text-xl text-brand-navy mb-4">My Projects</h2>
      {error ? (
        <ErrorBanner message={`Could not load projects: ${error.message}`} />
      ) : isLoading ? (
        <div className="text-muted-foreground py-8 text-center">Loading projects...</div>
      ) : myProjects.length === 0 ? (
        <div className="bg-card text-muted-foreground py-8 text-center">No projects yet.</div>
      ) : (
        <div className="space-y-3">
          {myProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default Home;
